/**
 * Barra de estado de la conexión.
 *
 * Solo aparece cuando hay algo que contar: sin conexión, con cosas pendientes de
 * enviar, o cuando el servidor rechazó alguna operación. Cuando todo está guardado
 * **no se muestra nada**, porque una barra que siempre dice «todo bien» deja de
 * mirarse y entonces no avisa cuando importa.
 */

import { AlertTriangle, CloudOff, Loader2, RefreshCw } from 'lucide-react';

import { connectionLabel, useOffline } from '../lib/offline';

export function OfflineBar(): React.JSX.Element | null {
  const online = useOffline((s) => s.online);
  const syncing = useOffline((s) => s.syncing);
  const pending = useOffline((s) => s.pending);
  const storageReady = useOffline((s) => s.storageReady);
  const rejected = useOffline((s) => s.rejected);
  const sync = useOffline((s) => s.sync);

  const { text, tone } = connectionLabel({ online, syncing, pending, storageReady });

  // Con todo al día y sin rechazos, no hay nada que decir.
  if (tone === 'ok' && rejected.length === 0) return null;

  const styles =
    tone === 'warn'
      ? 'border-ed-coral-deep/50 bg-ed-coral-deep/15 text-ed-coral-light'
      : tone === 'busy'
        ? 'border-ed-cyan/35 bg-ed-cyan/10 text-ed-cyan-light'
        : 'border-ed-success/35 bg-ed-success/10 text-ed-success';

  return (
    <div className={`border-b px-4 py-2 text-xs ${styles}`} role="status" aria-live="polite">
      <div className="mx-auto flex max-w-5xl items-center gap-2">
        {tone === 'warn' ? (
          <CloudOff size={14} className="shrink-0" aria-hidden="true" />
        ) : tone === 'busy' ? (
          <Loader2 size={14} className="shrink-0 animate-spin" aria-hidden="true" />
        ) : (
          <AlertTriangle size={14} className="shrink-0" aria-hidden="true" />
        )}

        <span className="flex-1">{text}</span>

        {online && !syncing && (
          <button
            type="button"
            onClick={() => void sync()}
            className="shrink-0 underline hover:no-underline"
            aria-label="Sincronizar ahora"
          >
            <RefreshCw size={12} className="mr-1 inline" aria-hidden="true" />
            Reintentar
          </button>
        )}
      </div>

      {/* Si el servidor rechazó algo, se dice cuál y por qué. */}
      {rejected.length > 0 && (
        <div className="mx-auto mt-1 max-w-5xl pl-5">
          <p className="font-medium">
            {rejected.length === 1
              ? 'Un movimiento no se pudo guardar en el servidor:'
              : `${rejected.length} movimientos no se pudieron guardar en el servidor:`}
          </p>
          <ul className="mt-0.5 space-y-0.5">
            {rejected.slice(0, 3).map((item) => (
              <li key={item.id}>· {item.error}</li>
            ))}
          </ul>
          <p className="mt-0.5">
            Siguen guardados en tu teléfono, no se han perdido. Corrígelos desde la pantalla de
            movimientos.
          </p>
        </div>
      )}
    </div>
  );
}
